import { useForm } from "react-hook-form";
import { Stack, Typography, useTheme } from "@mui/material";
import { Icon } from "@iconify/react";

import { Strings } from "@app/constants";
import { useRequestState } from "@app/hooks";
import { selectBrandState } from "@app/modules/categories/selector";
import { useEffect, useMemo } from "react";
import { findModelById } from "@app/modules/categories/slice";
import { useDispatch } from "react-redux";
import { sellCarDummyData } from "@app/modules/sellCar/sellCarDummyData";
import { RHFTextField } from "@app/components";

const Variant = ({ methods }: any) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const { data } = useRequestState({
    stateSelector: selectBrandState,
  });

  const mockData = sellCarDummyData;
  const brandId = methods.watch("brandId");
  const modelId = methods.watch("modelId");

  useEffect(() => {
    if (brandId) {
      dispatch(findModelById({ id: brandId }));
    }
  }, [brandId, dispatch]);

  useEffect(() => {
    methods.register("engineType");
    methods.register("transmission");
    methods.register("variant");
  }, [methods]);

  const selectedBrand = useMemo(
    () => data?.find((brand: any) => brand.id === brandId),
    [data, brandId]
  );

  const selectedModel = useMemo(
    () => selectedBrand?.models?.find((model: any) => model.id === modelId),
    [selectedBrand, modelId]
  );

  const variants = useMemo(() => {
    const rawVariants = selectedModel?.variant || [];
    if (Array.isArray(rawVariants)) {
      if (rawVariants.length === 1 && typeof rawVariants[0] === "string") {
        return rawVariants[0].split(",").map((v: string) => v.trim());
      }
      return rawVariants;
    }
    return [];
  }, [data]);

  const handleSelectVariant = (item: string) => {
    methods.setValue("variant", item);
  };

  const engineType = methods.watch("engineType");
  const transmission = methods.watch("transmission");
  const variant = methods.watch("variant");

  return (
    <>
      <Stack flexDirection={"column"} py={2}>
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: 22, sm: 24 },
              fontWeight: "bold",
              textAlign: "center",
            }}
          >
            {Strings.step.variants.engineSpecifications}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.grey[500], textAlign: "center" }}
          >
            {Strings.step.variants.TellUsAboutYour}
          </Typography>
        </Stack>
        <Stack
          sx={{
            mt: { xs: 5, sm: 5 },
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {mockData?.variants?.engine.map((item: any, index: any) => (
            <Stack
              onClick={() => methods.setValue("engineType", item)}
              key={index}
              sx={{
                px: { xs: 4, sm: 5 },
                py: 0.5,
                overflow: "hidden",
                border: `1px solid ${
                  engineType === item
                    ? theme.palette.primary.main
                    : theme.palette.grey[100]
                }`,
                borderRadius: 10,
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                textAlign: "center",
                flexShrink: 0,
                backgroundColor:
                  engineType === item
                    ? theme.palette.primary.main
                    : "transparent",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  py: 1,
                  fontWeight: 600,
                  color:
                    engineType === item
                      ? theme.palette.common.white
                      : theme.palette.grey[900],
                }}
              >
                {item}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Stack flexDirection={"column"} py={3} mt={2}>
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: "bold",
            }}
          >
            {Strings.step.variants.transmissionType}
          </Typography>
        </Stack>
        <Stack
          sx={{
            mt: 2,
            flexDirection: "row",
            flexWrap: "wrap",
            gap: { xs: 2, sm: 4 },
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {mockData?.variants?.transmission.map((item: any, index: any) => (
            <Stack
              onClick={() => methods.setValue("transmission", item.name)}
              key={index}
              sx={{
                width: { xs: 200, sm: 300, md: 350 },
                height: { xs: 100, sm: 150 },
                overflow: "hidden",
                borderRadius: 1,
                cursor: "pointer",
                border:
                  transmission === item.name
                    ? `1px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.common.black}`,
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                textAlign: "center",
              }}
            >
              <Icon
                icon={item.image}
                fontSize={48}
                style={{
                  color:
                    transmission === item.name
                      ? theme.palette.primary.main
                      : theme.palette.grey[900],
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  py: 1,
                  fontWeight: 600,
                  color:
                    transmission === item.name
                      ? theme.palette.primary.main
                      : theme.palette.grey[900],
                }}
              >
                {item.name}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Stack flexDirection={"column"} py={3} mt={2}>
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: "bold",
            }}
          >
            {Strings.step.variants.selectVariant}
          </Typography>
        </Stack>
        <Stack
          sx={{
            mt: 2,
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {variants?.map((item: any, index: any) => (
            <Stack
              onClick={() => handleSelectVariant(item)}
              key={index}
              sx={{
                width: 200,
                height: 100,
                overflow: "hidden",
                borderRadius: 1,
                cursor: "pointer",
                border:
                  variant === item
                    ? `1px solid ${theme.palette.primary.main}`
                    : `1px solid ${theme.palette.common.black}`,
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                textAlign: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color:
                    variant === item
                      ? theme.palette.primary.main
                      : theme.palette.grey[900],
                }}
              >
                {item}
              </Typography>
            </Stack>
          ))}
          {variants?.length === 0 && (
            <Typography color="error">No Variants for this Model</Typography>
          )}
        </Stack>
      </Stack>

      <Stack flexDirection={"column"} py={3} mt={2}>
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: "bold",
            }}
          >
            {Strings.step.variants.color}
          </Typography>
        </Stack>
        <Stack
          sx={{
            mt: 2,
            flexDirection: "row",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <RHFTextField
            name="color"
            placeholder="Enter Car Color"
            sx={{ width: { xs: "80%", md: "50%" } }}
          />
        </Stack>
      </Stack>
    </>
  );
};

export { Variant };
